import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { sanitizarDescricaoReleaseNote } from '../lib/sanitizar-release-note';

/** Rotas onde o modal "O que há de novo" NÃO deve aparecer. */
const ROTAS_SEM_MODAL = ['/login', '/register', '/verify-otp', '/escolher-tenant', '/verify-email',
  '/forgot-password', '/reset-password', '/unblock-account', '/accept-invite',
  '/trial-expirado'];

/**
 * Modal "O que há de novo" — REQ-012 Adendo / AB#64.
 *
 * Após login, busca `/minha-conta/release-notes` (não vistas) e abre o modal
 * para o primeiro item. "Entendido" marca como visto via API; modal avança
 * para a próxima não vista ou fecha. Não interfere se o usuário não tem
 * sessão íntegra (apenas faz fetch quando há access_token).
 *
 * SUPER_ADMIN é quem publica os releases — não recebe o modal (o backend
 * já filtra, e o front evita até a chamada).
 *
 * [AB#566] `preview`: quando presente, o MESMO componente renderiza esse
 * conteúdo (ainda não salvo, vindo do formulário de criar/editar) em vez
 * de buscar da API — usado pelo botão "Pré-visualizar" de
 * `AdminReleaseNotes.tsx`. Não é uma segunda implementação: é o modal
 * real que todo usuário vê, só alimentado de outra fonte.
 */

interface ReleaseNote {
  id: number;
  versao: string;
  data_lancamento: string;
  descricao: string;
}

export interface PreviewReleaseNote {
  versao: string;
  data_lancamento: string;
  descricao: string;
}

interface ReleaseNotesModalProps {
  preview?: PreviewReleaseNote;
  onFecharPreview?: () => void;
}

/** `2026-09-10` (ou vazio, ainda digitando no form) → `10/09/2026` ou `—`. */
function dataOuTraco(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

export function ReleaseNotesModal({ preview, onFecharPreview }: ReleaseNotesModalProps = {}): JSX.Element | null {
  const access_token = useAuthStore((s) => s.access_token);
  const usuario = useAuthStore((s) => s.usuario);
  const { pathname } = useLocation();
  const [pendentes, setPendentes] = useState<ReleaseNote[]>([]);
  const [carregando, setCarregando] = useState(false);

  const ocultoNestaRota = ROTAS_SEM_MODAL.some((r) => pathname.startsWith(r));

  const carregar = useCallback(async () => {
    if (preview) return; // [AB#566] modo preview não busca nada da API.
    if (!access_token || !usuario || ocultoNestaRota || usuario.perfil === 'SUPER_ADMIN') {
      setPendentes([]);
      return;
    }
    try {
      const res = await apiFetch('/minha-conta/release-notes');
      if (!res.ok) return;
      setPendentes((await res.json()) as ReleaseNote[]);
    } catch {
      /* ignore */
    }
  }, [access_token, usuario, ocultoNestaRota, preview]);

  useEffect(() => {
    if (preview) return;
    void carregar();
    // Refetch quando a aba volta a ficar visível — pega releases publicadas
    // enquanto o usuário estava em outra aba sem precisar de F5 (AB#64 bug 1).
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void carregar();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [carregar, preview]);

  if (!preview && (pendentes.length === 0 || ocultoNestaRota)) return null;
  const atual = preview ?? pendentes[0]!;

  async function entendido(): Promise<void> {
    setCarregando(true);
    try {
      await apiFetch(`/minha-conta/release-notes/${pendentes[0]!.id}/marcar-visto`, { method: 'POST' });
      // Avança para a próxima — não recarrega da rede.
      setPendentes((arr) => arr.slice(1));
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-900/70 p-4"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-2xl">
        <header className="border-b border-slate-200 px-6 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
            {preview ? 'Pré-visualização — O que há de novo' : 'O que há de novo'}
          </p>
          <h1 className="mt-1 text-xl font-bold text-slate-900">Versão {atual.versao || '—'}</h1>
          <p className="mt-1 text-xs text-slate-500">Lançamento {dataOuTraco(atual.data_lancamento)}</p>
        </header>
        <div className="flex-1 overflow-auto px-6 py-4">
          <div
            className="release-note-content"
            dangerouslySetInnerHTML={{ __html: sanitizarDescricaoReleaseNote(atual.descricao) }}
          />
        </div>
        <footer className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-3">
          {!preview && pendentes.length > 1 && (
            <span className="text-xs text-slate-500">
              {pendentes.length - 1} novidades restantes
            </span>
          )}
          <button
            type="button"
            onClick={preview ? onFecharPreview : () => void entendido()}
            disabled={!preview && carregando}
            className="inline-flex h-9 items-center rounded-md bg-brand-500 px-4 text-sm font-bold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {preview ? 'Fechar' : carregando ? '…' : 'Entendido'}
          </button>
        </footer>
      </div>
    </div>
  );
}
